import {
  FormChoiceOption,
  FormQuestion,
  FormQuestionType,
  FormTemplate,
  FormTemplateAutomationUpdate,
  FormThemeImageType,
  ProjectTemplatePhase,
} from 'api/form/types';
import { HttpClient } from 'api/http-client';
import { TemplateId } from 'api/models';
import { DefaultFormQuestionValues } from 'components/organisms/templates/forms/form-builder/utils';

const baseUrl = '/forms';

export interface CreateFormOptionPayload {
  questionId: number;
  optionLabel: string;
  color?: string;
}
export interface UpdateFormQuestionPositionPayload {
  branchingFrom?: number;
  position: number;
}
export type CreateFormQuestionPayload =
  | DefaultFormQuestionValues
  | {
      formQuestionType: FormQuestionType;
      projectFieldId?: number;
      taskFieldId?: number;
      templateId: number;
      questionText: string;
      description: string;
    };

export interface UploadThemeImagePayload {
  file: File;
  data: {
    name: string;
    imageType: 'BACKGROUND_IMAGE' | 'HEADER_IMAGE';
    templateId: number;
  };
}

export interface FormThemeImageUploadResponse {
  imageType: FormThemeImageType;
  location: string;
  name: string;
  thumbnailLocation: string;
}
export class FormTemplateClient extends HttpClient {
  public getFormTemplateList = (): Promise<{ templates: FormTemplate[] }> =>
    this.get(`${baseUrl}/template`);

  public getFormTemplate = (templateId: TemplateId): Promise<FormTemplate> =>
    this.get(`${baseUrl}/${templateId[1]}`);

  public createFormTemplate = (
    formTemplatePayload: Partial<FormTemplate>,
  ): Promise<FormTemplate> => this.post(baseUrl, formTemplatePayload);

  public updateFormTemplate = (
    formTemplatePayload: Partial<FormTemplateAutomationUpdate>,
    templateId: TemplateId,
  ): Promise<FormTemplate> => {
    const payloadWithoutUpdateInstance = { ...formTemplatePayload };

    delete payloadWithoutUpdateInstance.updateAllInstance;

    return this.put(
      `${baseUrl}/${templateId[1]}?updateAllInstance=${
        formTemplatePayload.updateAllInstance ?? false
      }`,
      payloadWithoutUpdateInstance,
    );
  };

  public duplicateFormTemplate = (templateId: number): Promise<FormTemplate> =>
    this.post(`${baseUrl}/${templateId}/clone`);

  public deleteFormTemplate = (templateId: TemplateId) =>
    this.delete(`${baseUrl}/${templateId[1]}`);

  public createFormQuestion = (
    formQuestionPayload: CreateFormQuestionPayload,
  ): Promise<FormQuestion> => this.post('/questions', formQuestionPayload);

  public updateFormQuestion = (
    questionId: number,
    payload: Partial<FormQuestion>,
  ): Promise<FormQuestion> => this.put(`/questions/${questionId}`, payload);

  public updateFormQuestionPosition = (
    questionId: number,
    payload: Partial<FormQuestion>,
  ): Promise<FormQuestion> =>
    this.put(`/questions/${questionId}/rearrange`, payload);

  public deleteFormQuestion = (questionId: number) =>
    this.delete(`/questions/${questionId}`);

  public createFormOption = (
    createFormOptionPayload: CreateFormOptionPayload,
  ): Promise<FormChoiceOption> =>
    this.post('/options', createFormOptionPayload);

  public deleteOption = (optionId: number) =>
    this.delete(`options/${optionId}`);

  public updateFormOption = (
    optionId: number,
    payload: Partial<FormChoiceOption>,
  ): Promise<FormChoiceOption> => this.put(`/options/${optionId}`, payload);

  public updateFormOptionPosition = (
    optionId: number,
    payload: Partial<FormChoiceOption>,
  ): Promise<FormChoiceOption> =>
    this.put(`/options/${optionId}/rearrange`, payload);

  public deleteOptionBranch = (optionId: number) =>
    this.delete(`/options/${optionId}/branch`);

  public uploadFormThemeImage = ({
    file,
    data,
  }: UploadThemeImagePayload): Promise<FormThemeImageUploadResponse> => {
    const formData = new FormData();
    const fileMeta = {
      name: data.name,
      imageType: data.imageType,
      templateId: data.templateId,
    };

    const json = JSON.stringify(fileMeta);
    const blob = new Blob([json], {
      type: 'application/json',
    });

    formData.append('file', file);
    formData.append('request', blob);

    return this.post(`${baseUrl}/${data.templateId}/theme/image`, formData);
  };

  public deleteThemeAttachment = (
    templateId: number,
    imageType: FormThemeImageType,
  ) => this.delete(`${baseUrl}/${templateId}/theme/image/${imageType}`);

  public getFormAutomationProjectTemplateOptions = (): Promise<{
    optionList: ProjectTemplatePhase[];
  }> => this.get(`${baseUrl}/automation/options`);
}

export const FormTemplateApi = new FormTemplateClient();
